import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Trophy } from 'lucide-react';

const CONFETTI_COLORS = [
  'var(--foundation-orange-500)',
  'var(--foundation-orange-300)',
  'var(--foundation-base-darkbase-dark-300)',
  'var(--foundation-calm-blue-300)',
  'var(--foundation-calm-blue-600)',
];

function ConfettiPiece({ delay, left }: { delay: number; left: number }) {
  const color = useMemo(
    () => CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    [],
  );
  const size = useMemo(() => 6 + Math.random() * 6, []);
  const drift = useMemo(() => `${Math.round((Math.random() - 0.5) * 160)}px`, []);
  const swayOne = useMemo(() => `${Math.round((Math.random() - 0.5) * 110)}px`, []);
  const swayTwo = useMemo(() => `${Math.round((Math.random() - 0.5) * 90)}px`, []);
  const rotation = useMemo(() => 420 + Math.round(Math.random() * 420), []);
  const duration = useMemo(() => `${3 + Math.random() * 4.5}s`, []);
  const rotationMidOne = useMemo(() => `${Math.round(rotation * 0.35)}deg`, [rotation]);
  const rotationMidTwo = useMemo(() => `${Math.round(rotation * 0.7)}deg`, [rotation]);

  const confettiStyle = useMemo(
    () =>
      ({
        left: `${left}%`,
        animationDelay: `${delay}s`,
        width: size,
        height: size * 2.5,
        backgroundColor: color,
        borderRadius: 2,
        '--confetti-drift': drift,
        '--confetti-sway-1': swayOne,
        '--confetti-sway-2': swayTwo,
        '--confetti-rotation': `${rotation}deg`,
        '--confetti-rotation-mid-1': rotationMidOne,
        '--confetti-rotation-mid-2': rotationMidTwo,
        '--confetti-duration': duration,
      }) as CSSProperties,
    [color, delay, drift, duration, left, rotation, rotationMidOne, rotationMidTwo, size, swayOne, swayTwo],
  );

  return (
    <div
      className="absolute top-0 opacity-0 animate-confetti-fall"
      style={confettiStyle}
      aria-hidden="true"
    />
  );
}

interface HeroSectionProps {
  courseName: string;
  completedAt?: string | null;
  description?: string | null;
}

const formatCompletionDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export function HeroSection({ courseName, completedAt, description }: HeroSectionProps) {
  const [show, setShow] = useState(false);
  const formattedCompletionDate = useMemo(
    () => formatCompletionDate(completedAt),
    [completedAt],
  );
  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 28 }, (_, index) => ({
        id: index,
        left: 2 + Math.random() * 96,
        delay: Math.random() * 2,
      })),
    [],
  );

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      aria-label="Course completion celebration"
      className="relative overflow-hidden pt-8 pb-20 px-6 text-center"
    >
      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {confettiPieces.map((piece) => (
          <ConfettiPiece key={piece.id} delay={piece.delay} left={piece.left} />
        ))}
      </div>

      <div
        className={`max-w-2xl mx-auto transition-all duration-700 ease-out ${
          show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40 mb-8">
          <Trophy className="h-4 w-4 text-emerald-700" aria-hidden="true" />
          <span className="text-sm font-semibold tracking-wide text-emerald-700 uppercase">
            Course Completed
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight text-balance mb-4">
          {courseName}
        </h1>

        {/* Date */}
        {formattedCompletionDate ? (
          <p className="text-muted-foreground text-base mb-6">
            Completed on {formattedCompletionDate}
          </p>
        ) : null}

        {/* Celebratory message */}
        <p className="text-lg md:text-xl text-foreground/80 leading-relaxed max-w-lg mx-auto text-pretty">
          {description?.trim() ||
            'Outstanding work. You completed this course and built meaningful momentum. Keep it going.'}
        </p>
      </div>
    </section>
  );
}
